import {
  Box,
  Button,
  CloseButton,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaArrowDown, FaArrowUp, FaPlus, FaTrash } from "react-icons/fa";
import { processStatus } from "../../Configurations/tabs/types";
import { ProcessFunnelStage } from "../../../models/Management";
import {
  deleteStage,
  getStages,
  reorderStages,
  saveStage,
  updateStage,
} from "../../../services/managementService";
import { crm } from "../../../theme";
import SearchableSelect from "../../../components/SearchableSelect";

const PALETTE = [
  "#3182ce",
  "#38a169",
  "#dd6b20",
  "#805ad5",
  "#e53e3e",
  "#319795",
  "#d69e2e",
  "#94a3b8",
];

// CANCELADO é status terminal e fica sempre fora do funil (nunca selecionável).
const ALL_STATUSES = processStatus.filter((s) => s.value !== "CANCELADO");

const splitStatuses = (s?: string) =>
  (s ?? "").split("|").map((x) => x.trim()).filter(Boolean);

const FunnelStageManager = ({
  isOpen,
  onClose,
  onChanged,
}: {
  isOpen: boolean;
  onClose: () => void;
  onChanged: () => void;
}) => {
  const toast = useToast();
  const [stages, setStages] = useState<ProcessFunnelStage[]>([]);
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(false);
  const [optionsVersion, setOptionsVersion] = useState(0);

  const load = async () => {
    try {
      const data = await getStages();
      setStages(data);
      setOptionsVersion((v) => v + 1);
    } catch (error) {
      console.log("error", error);
      toast({ title: "Não foi possível carregar as etapas.", status: "error" });
    }
  };

  useEffect(() => {
    if (isOpen) {
      load();
      setNewName("");
    }
  }, [isOpen]);

  const usedByOthers = (stageId: string | undefined) =>
    new Set(
      stages
        .filter((s) => s.id !== stageId)
        .flatMap((s) => splitStatuses(s.statuses))
    );

  const add = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try {
      const color = PALETTE[stages.length % PALETTE.length];
      await saveStage({ name: newName.trim(), order: stages.length, color, statuses: "" });
      setNewName("");
      await load();
      onChanged();
    } catch (error) {
      console.log("error", error);
      toast({ title: "Não foi possível criar a etapa.", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const rename = (id: string | undefined, name: string) => {
    setStages((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));
  };

  const persist = async (stage: ProcessFunnelStage) => {
    try {
      await updateStage(stage);
      onChanged();
    } catch (error) {
      console.log("error", error);
      toast({ title: "Não foi possível salvar o nome da etapa.", status: "error" });
      await load(); // reverte pro estado real do servidor
    }
  };

  const setStatuses = async (stage: ProcessFunnelStage, values: string[]) => {
    const updated = { ...stage, statuses: values.join("|") };
    setStages((prev) => prev.map((s) => (s.id === stage.id ? updated : s)));
    try {
      await updateStage(updated);
      onChanged();
    } catch (error) {
      console.log("error", error);
      toast({ title: "Não foi possível salvar os status da etapa.", status: "error" });
      await load(); // reverte pro estado real do servidor
    } finally {
      setOptionsVersion((v) => v + 1); // outras linhas precisam recalcular opções disponíveis
    }
  };

  const remove = async (id?: string) => {
    if (!id) return;
    try {
      await deleteStage(id);
      await load();
      onChanged();
      toast({ title: "Etapa removida.", status: "info", duration: 2000 });
    } catch (error) {
      console.log("error", error);
      toast({ title: "Não foi possível remover a etapa.", status: "error" });
    }
  };

  const swap = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= stages.length) return;
    const reordered = [...stages];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setStages(reordered);
    try {
      await reorderStages(reordered.map((s) => s.id!).filter(Boolean));
      onChanged();
    } catch (error) {
      console.log("error", error);
      toast({ title: "Não foi possível reordenar as etapas.", status: "error" });
      await load(); // reverte pro estado real do servidor
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px" boxShadow={crm.shadowLg}>
        <ModalHeader borderBottom={`1px solid ${crm.border}`}>
          <Flex justify="space-between" align="center">
            <Text fontWeight="700" fontSize="md" color={crm.text}>
              Gerenciar etapas do funil
            </Text>
            <CloseButton onClick={onClose} />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Text fontSize="sm" color={crm.textMuted} mb="14px">
            Cada etapa agrupa um ou mais status do processo. Um status só pode
            estar em uma etapa por vez — CANCELADO fica sempre fora do funil.
          </Text>

          <Flex gap="8px" mb="16px">
            <Input
              placeholder="Nome da nova etapa (ex: Produção, Embarque...)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              h="42px"
              borderColor={crm.border}
              borderRadius="10px"
              _focus={{ borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` }}
              fontSize="sm"
            />
            <Button
              leftIcon={<FaPlus />}
              bg={crm.brand}
              color="white"
              _hover={{ bg: crm.brandDark }}
              borderRadius="10px"
              onClick={add}
              isLoading={loading}
              minW="100px"
            >
              Criar
            </Button>
          </Flex>

          {stages.map((s, index) => {
            const excluded = usedByOthers(s.id);
            const options = ALL_STATUSES.filter((o) => !excluded.has(o.value));
            const currentValues = splitStatuses(s.statuses);
            return (
              <Box
                key={s.id}
                p="12px"
                mb="10px"
                border={`1px solid ${crm.border}`}
                borderRadius="10px"
                bg={crm.surface}
                _hover={{ borderColor: crm.borderStrong }}
                transition="border .1s"
              >
                <Flex align="center" gap="8px" mb="8px">
                  <Tooltip label="Cor da etapa" placement="top">
                    <Box
                      w="14px"
                      h="14px"
                      borderRadius="full"
                      bg={s.color || crm.brand}
                      flexShrink={0}
                    />
                  </Tooltip>
                  <Input
                    size="sm"
                    value={s.name}
                    onChange={(e) => rename(s.id, e.target.value)}
                    onBlur={() => persist(s)}
                    variant="flushed"
                    fontWeight="600"
                    color={crm.text}
                    borderColor="transparent"
                    _focus={{ borderColor: crm.brand }}
                    fontSize="sm"
                  />
                  <IconButton
                    aria-label="Subir"
                    icon={<FaArrowUp />}
                    size="xs"
                    variant="ghost"
                    color={crm.textMuted}
                    _hover={{ color: crm.text }}
                    isDisabled={index === 0}
                    onClick={() => swap(index, -1)}
                  />
                  <IconButton
                    aria-label="Descer"
                    icon={<FaArrowDown />}
                    size="xs"
                    variant="ghost"
                    color={crm.textMuted}
                    _hover={{ color: crm.text }}
                    isDisabled={index === stages.length - 1}
                    onClick={() => swap(index, 1)}
                  />
                  <IconButton
                    aria-label="Remover"
                    icon={<FaTrash />}
                    size="xs"
                    variant="ghost"
                    color={crm.textMuted}
                    _hover={{ color: crm.danger, bg: "#fef2f2" }}
                    onClick={() => remove(s.id)}
                  />
                </Flex>
                <SearchableSelect
                  key={`${s.id}-${optionsVersion}`}
                  isMulti
                  menuPortal
                  placeholder="Status agrupados nessa etapa..."
                  options={options}
                  defaultMultiValues={currentValues.map((v) => ({ value: v, label: v }))}
                  onMultiChange={(vals) => setStatuses(s, vals.map((v) => v.value))}
                />
              </Box>
            );
          })}

          {stages.length === 0 && (
            <Text color={crm.textFaint} fontSize="sm" textAlign="center" py="20px">
              Nenhuma etapa criada ainda.
            </Text>
          )}
        </ModalBody>
        <ModalFooter borderTop={`1px solid ${crm.border}`}>
          <Button onClick={onClose} variant="ghost" color={crm.textMuted} borderRadius="10px">
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FunnelStageManager;
